/**
 * Utility functions for contenteditable elements used in chat input components.
 */

/**
 * Extract plain text from a contenteditable element, skipping instruction placeholder elements.
 */
export function getPlainTextFromElement(element: HTMLElement): string {
  let text = '';
  // eslint-disable-next-line no-bitwise
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null);

  let node = walker.nextNode();
  while (node) {
    const isPlaceholder =
      (node instanceof HTMLElement && node.dataset.instructionPlaceholder) ||
      (node.nodeType === Node.TEXT_NODE && (node.parentElement as HTMLElement)?.dataset?.instructionPlaceholder);

    if (!isPlaceholder) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeName === 'BR') {
        text += '\n';
      } else if (node.nodeName === 'DIV' && text.length > 0 && !text.endsWith('\n')) {
        // Divs typically create line breaks in contenteditable
        text += '\n';
      }
    }
    node = walker.nextNode();
  }

  return text;
}

/**
 * Save cursor position in a contenteditable element.
 */
export function saveCursorPosition(element: HTMLElement): number {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return 0;

  const range = selection.getRangeAt(0);
  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(element);
  preCaretRange.setEnd(range.endContainer, range.endOffset);
  return preCaretRange.toString().length;
}

/**
 * Restore cursor position in a contenteditable element.
 */
export function restoreCursorPosition(element: HTMLElement, position: number): void {
  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();
  let currentPos = 0;
  let found = false;

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

  let node = walker.nextNode();
  while (node && !found) {
    const textLength = node.textContent?.length ?? 0;
    if (currentPos + textLength >= position) {
      range.setStart(node, position - currentPos);
      range.collapse(true);
      found = true;
    } else {
      currentPos += textLength;
    }
    node = walker.nextNode();
  }

  if (!found) {
    // Position at end if not found
    range.selectNodeContents(element);
    range.collapse(false);
  }

  selection.removeAllRanges();
  selection.addRange(range);
}
